import { useMemo } from "react";
import type { DateRange } from "@common/helpers/date";
import type { HistoryDailyCountsResponse } from "@common/types/auditLog";
import type { ChunkQueriesResult } from "@api/combineChunkQueries";
import { toLocalDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import type {
    DensityChartBar,
    DensityChartSlice,
    DensityChartWindow,
} from "@design-system/DensityChart/DensityChart.lib";
import type { DensityChartBucketUnit } from "@design-system/DensityChart/densityBuckets.lib";
import { slideOver, toSegmentClick } from "./activityTimelineClicks.lib";
import {
    ActivityTimelineChart,
    toTimelineSeries,
} from "./ActivityTimelineChart";
import { mergeChunkRows } from "./activityTimelineChunks.lib";
import { formatHistoryStart } from "./activityTimelineHistoryStart.lib";
import type { TimelinePerson } from "./activityTimelinePeople.lib";
import type { ActorPickFilter } from "./activityTimelineNarrowedCounts.lib";
import { toTimelineHighlight } from "./activityTimelinePicks.lib";
import type { ActivityTimelineRange } from "./activityTimelineRange.lib";
import type {
    ActivityTimelineSeries,
    TimelineSeriesKey,
} from "./activityTimelineSeries.lib";
import { useChartedPeople } from "./useChartedPeople";

interface ActivityPageTimelineProps<Filters extends object> {
    /** One counts query per chunk, in `chunks`' order. */
    counts: ChunkQueriesResult<HistoryDailyCountsResponse>;
    /** From `useActivityTimelineCountsParams`, with `people` and `onDrawnDaysChange`. */
    chunks: readonly DensityChartWindow[];
    onDrawnDaysChange: (drawnDays: DensityChartWindow) => void;
    /** From `useActivityTimelineCountsParams`, matching the counts' breakdown. */
    people: readonly TimelinePerson[];
    /** Updated by's picks; see `toTimelineHighlight` for the segments they draw selected inside the window. */
    picks: ActorPickFilter;
    /** The series this page charts, bottom first. */
    series: readonly ActivityTimelineSeries[];
    /** The saved density, the closest the chart shows. */
    closestUnit: DensityChartBucketUnit;
    /** The unit the chart shows for the window, never saved. */
    onShownUnitChange: (unit: DensityChartBucketUnit) => void;
    range: ActivityTimelineRange;
    /** Where a drag would drop the window, which the selection follows meanwhile. */
    previewWindow: DensityChartWindow | null;
    today: string;
    onWindowChange: (window: DensityChartWindow) => void;
    onWindowPreview: (window: DensityChartWindow | null) => void;
    /** The page filters a slice click sets for its series or person; keep it stable, since the selection reruns when it changes. */
    toSeriesFilters: (key: TimelineSeriesKey) => Filters;
    /** The page filters with Updated by cleared, which a click on a selected segment sets. */
    clearedActorFilters: Filters;
    onFiltersChange: (filters: Filters) => void;
    /** The zone's sentence for the formatted start, e.g. "Oct 19" or "Aug 19, 2025", when a join date starts the history. */
    describeHistoryStart?: (start: string) => string;
}

/**
 * An Activity page's timeline: the segments `toTimelineHighlight` picks
 * draw selected inside the window, or the window a drag previews. A
 * click on a selected segment clears Updated by; one on another segment
 * applies the page's filters for its series or person. A click on a bar not
 * wholly inside the window, on a segment or the track above it, slides the
 * window over it.
 */
export function ActivityPageTimeline<
    Filters extends Partial<DateRange> & ActorPickFilter,
>({
    counts: { responses, ownResponses, errors, isPlaceholderData, isLoading },
    chunks,
    onDrawnDaysChange,
    people,
    picks,
    series,
    closestUnit,
    onShownUnitChange,
    range: { window, historyStart, historyStartedBy },
    previewWindow,
    today,
    onWindowChange,
    onWindowPreview,
    toSeriesFilters,
    clearedActorFilters,
    onFiltersChange,
    describeHistoryStart,
}: ActivityPageTimelineProps<Filters>) {
    const chartedPeople = useChartedPeople({ people, isPlaceholderData });
    const { rows, isAnyChunkLoaded } = useMemo(
        () => mergeChunkRows({ responses, ownResponses }),
        [responses, ownResponses],
    );
    const timelineSeries = useMemo(
        () => toTimelineSeries({ people: chartedPeople, series }),
        [chartedPeople, series],
    );
    const { start: selectedStart, end: selectedEnd } = previewWindow ?? window;
    const highlight = useMemo(
        () =>
            toTimelineHighlight({
                picks,
                range: { start: selectedStart, end: selectedEnd },
                series: timelineSeries,
                toClickPicks: toSeriesFilters,
            }),
        [picks, selectedStart, selectedEnd, timelineSeries, toSeriesFilters],
    );
    const loadedDays = useMemo(
        () => chunks.filter((_, index) => ownResponses[index] !== undefined),
        [chunks, ownResponses],
    );
    const staleDays = useMemo(
        () =>
            chunks.filter(
                (_, index) =>
                    responses[index] !== undefined &&
                    ownResponses[index] === undefined,
            ),
        [chunks, responses, ownResponses],
    );
    const failedDays = useMemo(
        () => chunks.filter((_, index) => !!errors[index]),
        [chunks, errors],
    );
    const bounds = { start: historyStart, end: today };
    const showSeries = ({ seriesId, window: bar }: DensityChartSlice) => {
        const clicked = timelineSeries.find(({ id }) => id === seriesId);
        if (!clicked) {
            return;
        }
        const next = toSegmentClick({
            highlight,
            seriesId,
            bar,
            window,
            bounds,
        });
        onFiltersChange(
            next.kind === "cleared"
                ? clearedActorFilters
                : {
                      ...toSeriesFilters(clicked),
                      ...(next.window !== window &&
                          toLocalDateRange(next.window)),
                  },
        );
    };
    const showBar = ({ window: bar }: DensityChartBar) => {
        const next = slideOver({ bar, window, bounds });
        if (next !== window) {
            onWindowChange(next);
        }
    };
    const start = formatHistoryStart({ start: historyStart, today });
    const beforeStartMessage =
        historyStartedBy === "limit"
            ? `Your activity history starts on ${start}.`
            : historyStartedBy && describeHistoryStart?.(start);

    return (
        <ActivityTimelineChart
            rows={rows}
            series={timelineSeries}
            startDate={historyStart}
            endDate={today}
            bucketUnit={closestUnit}
            onShownBucketUnitChange={onShownUnitChange}
            window={window}
            onWindowChange={onWindowChange}
            onWindowPreview={onWindowPreview}
            onDrawnDaysChange={onDrawnDaysChange}
            onSliceClick={showSeries}
            onBarClick={showBar}
            highlight={highlight}
            loadedDays={loadedDays}
            failedDays={failedDays}
            staleDays={staleDays}
            isLoading={
                !isAnyChunkLoaded && (responses.length === 0 || isLoading)
            }
            beforeStartMessage={beforeStartMessage}
        />
    );
}
